import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './FileShare.module.css';

import { useFormContext } from 'react-hook-form';
import SimpleBar from 'simplebar-react';

import { useAppSelector } from '../../../../hooks/useRedux';
import useModal from '../../../../hooks/useModal';
import { useSlice } from '../../../../hooks/useSlice';
import useSimpleBar from '../../../../hooks/useSimpleBar';

import { selectMenu } from '../../../../features/infoSlice';

import {
  libApiSlice,
  useGetSharedLinkQuery,
  useGetSharedUsersQuery,
  useLibraryInitQuery,
  useValidateUsersMutation,
} from '../../../../services/library';
import { driveApiSlice } from '../../../../services/drive';

import {
  FileShareUserSearchResult,
  WaitingUser,
} from '../../../../types/library';
import Item from '../Item';
import Button from '../../../Button';
import Input from '../../../Input';
import Options from './Options';

import {
  NO_SHARED_LINK,
  NO_SHARED_USER,
  SHARED_LINK,
  CREATED_SHARED_LINK,
  SHARED_USER,
  COPY,
  SHARING_STOP,
  LEARNING_ANALYSIS,
  ADD,
  INSERT_SHARE_USER,
  INSERT_SHARE_USER_DESC,
  DELETE,
  SHARE,
  NOT_SIGNEDUP_USER,
  ACCOUNT_INACTIVE_MESSAGE,
  SHARED_WITH_USER,
  PREV,
  NEXT,
} from '../../../../constants';

import {
  VITE_URL,
  checkEmailPattern,
  copyToClipboard,
  limitMaxLen,
} from '../../../../utils/util';
import { selectUserEmail } from '../../../../features/authSlice';
import { isOfType } from '../../../../utils/typeGuard';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { TooltipWrapper } from '../../../Tooltip';

interface FileShare {
  name: string;
  id: string;
  type: string;
}

interface Share {
  [key: string]: {
    title: string;
    msg: string;
    emptyMsg: string;
    hasHelpIcon: boolean;
  };
}

interface ShareLink {
  link: string;
  expDateYn: 0 | 1;
  expDate: string;
  pwdYn: 0 | 1;
  pwd: string;
  analysisYn: 0 | 1;
  printYn: 0 | 1;
  captureYn: 0 | 1;
}

interface ShareUser {
  expDateYn: 0 | 1;
  expDate: string;
  emailList: EmailList[];
  printYn: 0 | 1;
  captureYn: 0 | 1;
}

interface EmailList {
  type: 1 | 2 | 3;
  email: string;
  userYn?: 0 | 1;
  enableYn?: 0 | 1;
  dormantYn?: 0 | 1;
}

const shares: Share = {
  shareLink: {
    title: SHARED_LINK,
    msg: CREATED_SHARED_LINK,
    emptyMsg: NO_SHARED_LINK,
    hasHelpIcon: false,
  },
  shareUser: {
    title: SHARED_USER,
    msg: SHARED_WITH_USER,
    emptyMsg: NO_SHARED_USER,
    hasHelpIcon: true,
  },
};

export default function FileShare({ id }: FileShare) {
  const cx = classNames.bind(styles);
  const { selectSelectedId, selectSingleItem } = useSlice();
  const { toggleModal } = useModal();
  const { setValue } = useFormContext();
  const [simplebarRef] = useSimpleBar();

  const menu = useAppSelector(selectMenu);
  const singleItem = useAppSelector(selectSingleItem);
  const projectId = useAppSelector(selectSelectedId);
  const account = useAppSelector(selectUserEmail);

  const {
    data: sharedLink,
    isLoading: isSharedLinkLoading,
    refetch: getSharedLink,
  } = useGetSharedLinkQuery(projectId);
  const {
    data: sharedUsers,
    isLoading: isSharedUsersLoading,
    refetch: getSharedUsers,
  } = useGetSharedUsersQuery(projectId);
  const {
    data: libraryInit,
    isLoading: isLibraryInitLoading,
    refetch: refetchLibraryInit,
  } = useLibraryInitQuery();

  const singleItemShareLink =
    sharedLink || (isOfType(singleItem, 'share') && singleItem.share);
  const isLibrary = menu === 'library';

  const [shareLink, setShareLink] = useState<ShareLink>(singleItemShareLink);
  const [shareUser, setShareUser] = useState<ShareUser>(sharedUsers);

  const [isShowAddUser, setIsShowAddUser] = useState(
    !!shareUser?.emailList?.length
  );
  const [searchList, setSearchList] = useState<FileShareUserSearchResult[]>([]);
  const [waitingUserList, setWaitingUserList] = useState<WaitingUser[]>([]);
  const [validatedUserList, setValidatedUserList] = useState<EmailList[]>(
    shareUser?.emailList ?? []
  );
  const [isShowSearchResult, setIsShowSearchResult] = useState(false);
  const inputUserRef = useRef<HTMLInputElement>(null);

  const [getLibSharedData] = libApiSlice.useLazyGetNewLinkQuery();
  const [getDriveSharedData] = driveApiSlice.useLazyGetDriveSharedLinkQuery();

  const [getSearchUsers] = libApiSlice.useLazyGetSearchUsersQuery();
  const [vlidateUsers] = useValidateUsersMutation();

  const navigationPrevRef = useRef<HTMLButtonElement>(null);
  const navigationNextRef = useRef<HTMLButtonElement>(null);
  const [groupList, setGroupList] = useState<
    (WaitingUser & { alias: string })[]
  >([]);

  const linkUrl = `${VITE_URL}/share?link=${
    shareLink?.link
  }&resourceYn=${+!isLibrary}`;

  const share = async (key: string) => {
    if (key === 'shareLink') {
      let newShareLink = null;
      try {
        if (isLibrary) {
          newShareLink = await getLibSharedData(projectId).unwrap();
        } else {
          newShareLink = await getDriveSharedData(projectId).unwrap();
        }
        setShareLink(newShareLink);
      } catch (error) {
        console.error('Failed to get Share Data', error);
      }
    } else {
      setIsShowAddUser(true);
    }
  };

  const toggleCheckbox = ({ target }: React.FormEvent<HTMLLabelElement>) => {
    const { id, checked } = target as HTMLInputElement;
    const key = id.split('.')[1] || id.split('.')[0];
    const isExpDateYn = id.includes('expDateYn');
    const isPwdYn = id.includes('pwdYn');
    const type = isLibrary ? id.split('.')[0] : '';
    const idPrefix = isLibrary ? `${type}.` : '';

    setData(id, key, +checked);

    if (!checked) {
      if (isExpDateYn) {
        setValue(`${idPrefix}expDate`, '');
      } else if (isPwdYn) {
        setValue(`${idPrefix}pwd`, '');
      }
    }
  };

  const changePwd = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = target as HTMLInputElement;
    const key = id.split('.')[1] || id.split('.')[0];

    setData(id, key, limitMaxLen(value, 10));
  };

  const setData = (id: string, key: string, val: string | number | []) => {
    if (id.includes('shareUser')) {
      setShareUser({ ...shareUser, [key]: val });
    } else {
      setShareLink({ ...shareLink, [key]: val });
    }
    setValue(id, val);
  };

  const openModal = (name: string) => {
    toggleModal({
      id: 'confirmStopSharing',
      type: 'confirm',
      isOpen: true,
      name,
    });
  };

  const getSearchResult = async ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    const selectionStart = target.selectionStart ?? 0;
    const beforeCommaIdx = value.lastIndexOf(',', selectionStart - 1);
    const afterCommaIdx = value.indexOf(',', selectionStart);
    const endIdx = afterCommaIdx === -1 ? value.length : afterCommaIdx;
    const searchText = value
      .trim()
      .substring(beforeCommaIdx + 1, endIdx)
      .trim();

    if (searchText) {
      try {
        const searchResult = await getSearchUsers({
          projectId,
          search: searchText,
        }).unwrap();
        setSearchList(searchResult);
        setIsShowSearchResult(!!searchResult.length);
      } catch (error) {
        console.error('Failed to get search result', error);
      }
    } else {
      setSearchList([]);
      setIsShowSearchResult(false);
    }
  };

  const addUSer = async () => {
    const emailInput = inputUserRef.current as HTMLInputElement;
    const userList = emailInput.value
      .split(',')
      .filter((value) => value.trim())
      .reduce(
        (acc: { email: string }[], cur) =>
          validatedUserList.map(({ email }) => email).includes(cur.trim())
            ? acc
            : [...acc, { email: cur.trim() }],
        []
      );
    const unTypedList = userList.reduce(
      (acc: { email: string; type: 1 | 2 | 3 | null }[], cur) =>
        waitingUserList
          .map((waitingUser) => waitingUser.email)
          .includes(cur.email)
          ? acc
          : [...acc, { email: cur.email, type: null }],
      []
    );
    const validationList = [
      ...waitingUserList.reduce(
        (acc, cur) =>
          !acc.map(({ email }) => email).includes(cur.email) &&
          userList.map((user) => user.email).includes(cur.email)
            ? [...acc, cur]
            : acc,
        [...unTypedList]
      ),
    ];

    try {
      const newValidatedList: EmailList[] = await vlidateUsers({
        projectId,
        data: validationList,
      }).unwrap();
      const newFilteredValidatedList = newValidatedList.filter(
        ({ email, userYn }) =>
          !((!checkEmailPattern(email) && userYn === 0) || email === account)
      );
      const newUserList = [...validatedUserList, ...newFilteredValidatedList];

      setValidatedUserList(newUserList);

      setIsShowSearchResult(false);
      setWaitingUserList([]);
      emailInput.value = '';
    } catch (error) {
      console.error('Failed to validate users', error);
    }
  };

  const addGroup = (data: {
    type: 1 | 2 | 3;
    email: string;
    alias: string;
  }) => {
    const { email, type } = data;
    setValidatedUserList([...validatedUserList, { type, email }]);
  };

  function User({ user }: { user: FileShareUserSearchResult }) {
    const { type, email, alias, enableYn } = user;
    const emailInput = inputUserRef.current as HTMLInputElement;
    const emailRef = useRef<HTMLSpanElement>(null);
    const waitingEmailList = [
      ...emailInput.value
        .split(',')
        .filter((value) => value.trim())
        .map((value) => value.trim()),
      ...validatedUserList.map(({ email }) => email),
    ];

    const selectUser = () => {
      const emailInput = inputUserRef.current as HTMLInputElement;
      const inputValue = emailInput.value;
      const additionalWaitingList = inputValue
        .split(',')
        .map((value) => value.trim());
      const selectionStart = emailInput.selectionStart ?? 0;
      const beforeCommaIdx = emailInput.value.lastIndexOf(
        ',',
        selectionStart - 1
      );
      const newWaitingUserList = [...waitingUserList, { email, type }].reduce(
        (acc: WaitingUser[], cur) =>
          validatedUserList.map(({ email }) => email).includes(cur.email.trim())
            ? acc
            : [...acc, { email: cur.email.trim(), type: cur.type }],
        []
      );

      if (!additionalWaitingList.includes(email)) {
        if (inputValue.length > selectionStart) {
          const afterCommaIdx = inputValue.indexOf(',', selectionStart);
          emailInput.value =
            inputValue.substring(0, beforeCommaIdx + 1) +
            `${beforeCommaIdx === -1 ? '' : ' '}${email}` +
            (afterCommaIdx === -1 ? '' : inputValue.substring(afterCommaIdx));
        } else {
          emailInput.value = `${inputValue.substring(0, beforeCommaIdx + 1)}${
            beforeCommaIdx === -1 ? '' : ' '
          }${email}`;
        }
      }
      setWaitingUserList(newWaitingUserList);
      setIsShowSearchResult(false);
    };

    return (
      <li
        className={cx({
          'is--disabled': validatedUserList
            .map(({ email }) => email)
            .includes(email),
        })}
        onClick={selectUser}
      >
        <i className={cx('user_type', type)}></i>
        <span className={cx('text_clip')} ref={emailRef}>
          {email}
        </span>
        <span className={cx('text_clip')}>{alias}</span>
      </li>
    );
  }

  const removeUser = (user: string) => {
    const newEmailList = validatedUserList.filter(
      ({ email }) => email !== user
    );
    setValidatedUserList(newEmailList);
    setValue(
      'shareUser.emailList',
      newEmailList.map(({ type, email }) => ({ type, email }))
    );
  };

  function BtnStopShare({ type }: { type: string }) {
    return (
      <Button
        classnames={['btn', 'skyt', 'sharing', 'stop', type]}
        name={SHARING_STOP}
        handleClick={() => openModal(type)}
      ></Button>
    );
  }

  useEffect(() => {
    setShareLink(singleItemShareLink);
    if (isLibrary) {
      setValue('shareLink', singleItemShareLink);
    }
  }, [singleItemShareLink]);

  useEffect(() => {
    if (isLibrary) {
      setShareUser(sharedUsers);
      setValidatedUserList(sharedUsers?.emailList);
      // setWaitingUserList(sharedUsers?.emailList);
      setIsShowAddUser(!!sharedUsers?.emailList?.length);
      setValue('shareUser.expDate', sharedUsers?.expDate);
    }
  }, [sharedUsers]);

  useEffect(() => {
    setValue(
      'shareUser.emailList',
      validatedUserList?.reduce(
        (acc: EmailList[], { email, type, dormantYn }) =>
          dormantYn ? acc : [...acc, { email, type }],
        []
      )
    );
  }, [validatedUserList]);

  useEffect(() => {
    if (isLibrary) {
      setTimeout(() => {
        isSharedLinkLoading || getSharedLink();
        isSharedUsersLoading || getSharedUsers();
      }, 0);
      refetchLibraryInit();
    }
  }, []);

  useEffect(() => {
    libraryInit && setGroupList(libraryInit.studentGroupList);
  }, [libraryInit]);

  if (isSharedLinkLoading || isSharedUsersLoading || isLibraryInitLoading)
    return null;

  return (
    <>
      {Object.entries(shares).map(([key, obj]) => {
        const isShareLink = key === 'shareLink';
        let isShared = isShareLink ? !!shareLink?.link : isShowAddUser;

        if (!isLibrary && key === 'shareUser') return null;

        return (
          <Item
            id={id}
            title={obj.title}
            key={key}
            {...(shareLink?.link && !isShareLink && { classnames: ['mt70'] })}
          >
            <div className={cx('panel_row', 'column')}>
              <div className={cx('share_area')}>
                <div className={cx('desc', { bold: shareLink?.link })}>
                  {obj[isShared ? 'msg' : 'emptyMsg'].replace(
                    '{N}',
                    shareUser?.emailList?.length.toString() ?? 0
                  )}
                </div>
                {!isShared ? (
                  <Button
                    name={SHARE}
                    classnames={['skyt', 'sharing']}
                    handleClick={() => share(key)}
                  />
                ) : isShareLink ? (
                  <>
                    <div className={cx('share_url')}>
                      <Input
                        type="text"
                        id={`${isLibrary ? 'shareLink.' : ''}link`}
                        isHidden={true}
                        value={shareLink?.link}
                      />
                      <span className={cx('url', 'text_clip')}>{linkUrl}</span>
                      <TooltipWrapper
                        content={COPY}
                        onClick={() => copyToClipboard(linkUrl)}
                      >
                        {(tooltipProps) => (
                          <span
                            role="button"
                            className={cx('btn', 'copyurl')}
                            {...tooltipProps}
                          >
                            {COPY}
                          </span>
                        )}
                      </TooltipWrapper>
                    </div>

                    <Options
                      type="shareLink"
                      data={shareLink}
                      changeHandler={(e) => toggleCheckbox(e)}
                      setData={setData}
                      changePwd={(e) => changePwd(e)}
                    ></Options>
                    <BtnStopShare type={key} />

                    {/* {isLibrary && (
                      <div className={cx('share_analyse')}>
                        <div className={cx('cb_left')}>
                          <label>
                            <Input
                              type="checkbox"
                              id={'shareLink.analysisYn'}
                              value={shareLink?.analysisYn ?? 1}
                              isHidden={true}
                            />
                            <span className={cx('clabel')}>
                              {LEARNING_ANALYSIS}
                            </span>
                          </label>
                        </div>
                      </div>
                    )} */}
                  </>
                ) : (
                  <>
                    <div className={cx('insert_user')}>
                      <input
                        type="text"
                        className={cx('input_t', 'line')}
                        placeholder={INSERT_SHARE_USER}
                        onChange={getSearchResult}
                        ref={inputUserRef}
                      />
                      <button
                        type="button"
                        className={cx('btn', 'b_t', 'dull', {
                          'is--disabled': !inputUserRef?.current?.value.trim(),
                        })}
                        onClick={addUSer}
                      >
                        <span>{ADD}</span>
                      </button>
                      <span className={cx('desc')}>
                        {INSERT_SHARE_USER_DESC}
                      </span>
                      {isShowSearchResult && (
                        <div className={cx('overlay_list_area')}>
                          <SimpleBar forceVisible={true} ref={simplebarRef}>
                            <ul>
                              {searchList.map((user) => (
                                <User key={user.email} user={user} />
                              ))}
                            </ul>
                          </SimpleBar>
                        </div>
                      )}
                    </div>

                    {groupList.length ? (
                      <div className={cx('swiper-container')}>
                        <Swiper
                          modules={[Navigation]}
                          navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                          }}
                          slidesPerView="auto"
                          spaceBetween={8}
                          grabCursor={true}
                          onSwiper={(swiper) => {
                            setTimeout(() => {
                              if (
                                typeof swiper?.params?.navigation !== 'boolean'
                              ) {
                                const navigation = swiper?.params?.navigation;
                                if (navigation) {
                                  navigation.prevEl = navigationPrevRef.current;
                                  navigation.nextEl = navigationNextRef.current;
                                  swiper.navigation.init();
                                  swiper.navigation.update();
                                }
                              }
                            }, 0);
                          }}
                        >
                          {groupList.map(({ type, email, alias }) => (
                            <SwiperSlide
                              key={email}
                              className={cx('group-slide')}
                            >
                              <div
                                role="button"
                                className={cx('group-button', {
                                  'is--disabled': validatedUserList.some(
                                    (user) => user.email === email
                                  ),
                                })}
                                onClick={() => addGroup({ type, email, alias })}
                              >
                                {email}
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                        <div className={cx('slide_nav')}>
                          <button
                            ref={navigationPrevRef}
                            className={cx('slide_prev')}
                          >
                            <span className={cx('blind')}>{PREV}</span>
                          </button>
                          <button
                            ref={navigationNextRef}
                            className={cx('slide_next')}
                          >
                            <span className={cx('blind')}>{NEXT}</span>
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {!!validatedUserList.length && (
                      <>
                        <div className={cx('user_list')}>
                          <SimpleBar forceVisible={true} ref={simplebarRef}>
                            <ul>
                              {validatedUserList.map(
                                ({ email, type, userYn, dormantYn }) => (
                                  <li
                                    key={email}
                                    className={cx({ group: type === 3 })}
                                  >
                                    <span
                                      className={cx('text_clip', {
                                        wrong:
                                          (type === 1 && !userYn) || dormantYn,
                                      })}
                                      title={
                                        type === 1 && !userYn
                                          ? NOT_SIGNEDUP_USER
                                          : dormantYn
                                          ? ACCOUNT_INACTIVE_MESSAGE
                                          : email
                                      }
                                    >
                                      {email}
                                    </span>
                                    <span
                                      role="button"
                                      className={cx('btn', 'delete_user')}
                                      onClick={() => removeUser(email)}
                                    >
                                      {DELETE}
                                    </span>
                                  </li>
                                )
                              )}
                            </ul>
                          </SimpleBar>
                        </div>
                        <Options
                          type="shareUser"
                          data={shareUser}
                          changeHandler={(e) => toggleCheckbox(e)}
                          setData={setData}
                        ></Options>
                        <BtnStopShare type={key} />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </Item>
        );
      })}
    </>
  );
}
